/**
 * Attentive read receipts — SPEC-2026-09-internal-chat-pwa (T-27 · RF-22).
 *
 * A message counts as read only when a person could actually have seen it: the page is
 * visible AND has focus. A conversation left open in a background tab, a minimised
 * window or an installed app behind another one would otherwise acknowledge messages
 * nobody looked at, and the sender's "read" (and the reader's own unread badge) would
 * lie. While the page is not attentive, the READ is deferred; it is sent once, when the
 * page becomes attentive again.
 */

/** Visible and focused — the only state in which a READ may be sent. */
export function isDocumentAttentive(doc: Pick<Document, 'visibilityState' | 'hasFocus'> | null | undefined): boolean {
  if (!doc) return false;
  try {
    return doc.visibilityState === 'visible' && doc.hasFocus();
  } catch {
    return false;
  }
}

/**
 * Holds at most one pending READ. Requests collapse: ten messages arriving while the
 * tab is hidden produce a single READ when focus returns.
 */
export class ChatAttentiveRead {

  private pending: (() => void) | null = null;

  constructor(private readonly isAttentive: () => boolean) { }

  /** Whether a READ is waiting for attention. */
  get hasPending(): boolean {
    return this.pending !== null;
  }

  /** Sends now when attentive (and drops anything pending); defers otherwise. */
  request(send: () => void): boolean {
    if (this.isAttentive()) {
      this.pending = null;
      send();
      return true;
    }
    this.pending = send;
    return false;
  }

  /** Sends the pending READ if the page became attentive. */
  flush(): boolean {
    if (!this.pending || !this.isAttentive()) return false;
    const send = this.pending;
    this.pending = null;
    send();
    return true;
  }

  /** Drops the pending READ — the conversation it belonged to was left. */
  cancel(): void {
    this.pending = null;
  }
}
